import {useBundleBagTotal} from "./bundle.tsx";
import {Control, FieldErrors, SubmitHandler, useController, useForm} from "react-hook-form";
import {Divider, FormControl, FormLabel, IconButton, Input as JoyInput, Sheet, Stack, Switch, useTheme} from "@mui/joy";
import Grid from "@mui/material/Grid";
import {FormEvent, useCallback, useEffect, useRef, useState} from "react";
import {EditorMode, ReactHookFormValidationErrorAlert} from "../forms.tsx";
import {useTranslation} from "react-i18next";
import {NumericFormat} from "react-number-format";
import {
    Bundle,
    BundleExtras,
    BundlePatch,
    PatchJobRequest,
    PatchJobResponse,
    ShinwaJobExtras,
    useJobById,
    watchBundleByIdTqo,
    WideJobExtras
} from "../rpc/webrpc-client.gen.ts";
import {canvasToBlob, LocalPhoto, uploadPhoto} from "../photo.tsx";
import SignatureCanvas from "react-signature-canvas";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEraser} from "@fortawesome/pro-regular-svg-icons";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {deepEqual, useNavigate} from "@tanstack/react-router";
import {pickBy} from "lodash";
import {ActionButton} from "../components/ActionButton.tsx";
import {uuidv7} from "uuidv7";
import {
    useAttachPhotoToStampMutation,
    useMothership,
    usePatchBundleMutation,
    useStampBundleMutation
} from "../tq/tqx.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {useAuthenticator} from "../auth/authenticator.tsx";
import {z} from "zod/v4";
import {strToFixedPoint} from "../tq/tqutil.tsx";
import {useBomb} from "../bomb/bomb.tsx";
import {useInfallibleQuery} from "../util/useInfallibleQuery.tsx";
import {useSystemConfig} from "../rpc/sysconf.tsx";

export const BundleSurchargeForm = z.object({
    carrierBringCount: z.union([z.number(), z.literal("")]),
    carrierTakeCount: z.union([z.number(), z.literal("")]),
    bagBringCount: z.union([z.number(), z.literal("")]),
    bagTakeCount: z.union([z.number(), z.literal("")]),
    emptyBagTakeCount: z.union([z.number(), z.literal("")]),
    assistBringCount: z.union([z.number(), z.literal("")]),
    assistTakeCount: z.union([z.number(), z.literal("")]),
//    packingTime: string;
    containerBringCount: z.union([z.number(), z.literal("")]),
    containerTakeCount: z.union([z.number(), z.literal("")]),
    distanceFee: z.boolean(),
    dollyFee: z.boolean(),
    dockingFee: z.boolean(),
    signageFee: z.boolean(),
    halfDayFee: z.boolean(),
    fullDayFee: z.boolean(),
    eveningFee: z.boolean(),
    lateNightFee: z.boolean(),
    earlyMorningFee: z.boolean(),
});
export type BundleSurchargeForm = z.infer<typeof BundleSurchargeForm>;

export const BundleSurchargeBag = BundleSurchargeForm.partial().extend({
    signatureSlug: z.optional(z.string()),
});
export type BundleSurchargeBag = z.infer<typeof BundleSurchargeBag>;

function omitFalsiness(form: BundleSurchargeForm): BundleSurchargeBag {
    const rv
        = pickBy(form, (maybe) => !!maybe);
    return rv;
}

function makePatch({bundle, surchargeBag}: { bundle: Bundle, surchargeBag: BundleSurchargeBag }): BundlePatch | null {
    const oldExtras = bundle.extras;
    // this cast is needed because RIDL can't handle number | ""
    const newExtras = BundleExtras.parse({...oldExtras, ...surchargeBag});
    if (deepEqual(oldExtras, newExtras)) {
        return null;
    } else {
        return {extras: newExtras};
    }
}

export const BundleSurchargeFieldApprovalViewProps = z.object({
    bundleId: z.string(),
    mode: z.optional(EditorMode),
})
export type BundleSurchargeFieldApprovalViewProps = z.infer<typeof BundleSurchargeFieldApprovalViewProps>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IntegerFieldName = z.enum(["carrierBringCount",
    "carrierTakeCount",
    "bagBringCount",
    "bagTakeCount",
    "emptyBagTakeCount",
    "assistBringCount",
    "assistTakeCount",
    "containerBringCount",
    "containerTakeCount",
]);
type IntegerFieldName = z.infer<typeof IntegerFieldName>;

// type StringFieldName = ;

const booleanFieldNames = [
    "distanceFee",
    "dollyFee",
    "dockingFee",
    "signageFee",
    "halfDayFee",
    "fullDayFee",
    "eveningFee",
    "lateNightFee",
    "earlyMorningFee",
] as const;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BooleanFieldName = z.enum(booleanFieldNames);
type BooleanFieldName = z.infer<typeof BooleanFieldName>;

interface BundleSurchargeFieldProps<T extends IntegerFieldName | BooleanFieldName> {
    control: Control<BundleSurchargeForm>;
    fieldName: T;
    suffix?: string;
    readonly?: boolean;
    formErrors?: FieldErrors<BundleSurchargeForm>;
}

function IntegerField(
    {control, fieldName, suffix, readonly, formErrors}: BundleSurchargeFieldProps<IntegerFieldName>) {
    const {t} = useTranslation();
    const {field} = useController({
        name: fieldName,
        control,
    });
    // if (!isNumber(field.value)) {
    //     throw new Error(`expected number on field ${fieldName}, got ${field.value}`);
    // }
    return <FormControl>
        <FormLabel>{t(`bundleSurchargeForm.${fieldName}`)}</FormLabel>
        <NumericFormat
            decimalScale={0}
            value={field.value === 0 ? "" : field.value}
            onValueChange={(values) => {
                field.onChange({
                    target: {
                        name: field.name,
                        value: strToFixedPoint(values.value, 0),
                    }
                });
            }}
            customInput={JoyInput}
            slotProps={{input: {type: "number"}}}
            sx={{maxWidth: "8rem"}}
            endDecorator={suffix}
            valueIsNumericString={true}
            readOnly={readonly}
        />
        <ReactHookFormValidationErrorAlert error={formErrors ? formErrors[fieldName] : null}/>
    </FormControl>
}

// function StringField(
//     {
//         control, fieldName, suffix, readonly, formErrors
//     }: BundleSurchargeFieldProps<StringFieldName>
// ) {
//     const {t} = useTranslation();
//     const {field} = useController({
//         control,
//         name: fieldName,
//     });
//     return <FormControl>
//         <FormLabel>{t(`bundleSurchargeForm.${fieldName}`)}</FormLabel>
//         <JoyInput value={field.value} onChange={field.onChange}
//                   sx={{maxWidth: "12rem"}}
//                   disabled={readonly}
//                   endDecorator={suffix}
//         />
//         <FormValidationErrorAlert error={formErrors ? formErrors[fieldName] : null}/>
//     </FormControl>
// }

function BooleanField(
    {control, fieldName, readonly, formErrors}: BundleSurchargeFieldProps<BooleanFieldName>) {
    const {t} = useTranslation();
    const theme = useTheme();
    const {field} = useController({
        control,
        name: fieldName,
    });
    return <FormControl orientation="horizontal"
                        sx={{
                            flex: 1,
                            borderWidth: "2px",
                            borderStyle: "solid",
                            borderColor: theme.palette.primary[900],
                            p: 0.8
                        }}
                        color={field.value ? "primary" : "neutral"}>
        <FormLabel>{t(`bundleSurchargeForm.${fieldName}`)}</FormLabel>
        <Switch checked={field.value} onChange={field.onChange}
                disabled={readonly}/>
        <ReactHookFormValidationErrorAlert error={formErrors ? formErrors[fieldName] : null}/>
    </FormControl>
}

export function BundleSurchargeFieldApprovalView({bundleId, mode}: BundleSurchargeFieldApprovalViewProps) {
    const {t} = useTranslation();
    const theme = useTheme();
    const bomb = useBomb();
    const mama = useMothership();
    const sysConfTqrv = useSystemConfig();
    const navigate = useNavigate();
    const authenticator = useAuthenticator();
    const queryClient = useQueryClient();
    const bundleTqrv = useInfallibleQuery(watchBundleByIdTqo(mama, {bundleId}));
    const bundle = bundleTqrv.data;
    const totalBagsTqrv = useBundleBagTotal({bundleId});
    const totalBags = totalBagsTqrv.data ?? "";
    const {
        reset: resetForm,
        handleSubmit,
        control,
    } = useForm<BundleSurchargeForm>({
        defaultValues: {
            carrierBringCount: "",
            carrierTakeCount: "",
            bagBringCount: "",
            bagTakeCount: totalBags,
            emptyBagTakeCount: "",
            assistBringCount: "",
            assistTakeCount: "",
            containerBringCount: "",
            containerTakeCount: "",
            distanceFee: false,
            dollyFee: false,
            dockingFee: false,
            signageFee: false,
            halfDayFee: false,
            fullDayFee: false,
            eveningFee: false,
            lateNightFee: false,
            earlyMorningFee: false,
        },
    });
    const jobTqrv =
        useJobById(bundle ? {jobId: bundle.job.id} : undefined);
    const shinwaJobExtras = jobTqrv.data?.extras?.shinwa;
    useEffect(() => {
        if (shinwaJobExtras) {
            resetForm(prev => {
                const rv: BundleSurchargeForm = {
                    ...prev,
                    distanceFee: shinwaJobExtras.distanceFee ?? false,
                    dockingFee: shinwaJobExtras.docking ?? false,
                };
                return rv;
            });
        }
    }, [shinwaJobExtras, resetForm]);

    const [readonly]
        = useState<boolean>(mode !== "rw");

    const signatureRef = useRef<SignatureCanvas>(null);

    const {mutateAsync: performPatch} = usePatchBundleMutation();
    const {mutateAsync: performStamp} = useStampBundleMutation();
    const stampFieldApproved = useCallback(() =>
            performStamp({bundleId, flavor: "FIELD_COMPLETE_ACCEPT"}),
        [bundleId, performStamp]);
    const {mutateAsync: attachPhotoToFieldApprovalStamp} = useAttachPhotoToStampMutation();

    const {mutateAsync: performJobPatch} = useMutation({
        mutationFn: (req: PatchJobRequest) => mama.patchJob(req)
    });

    const onValidSubmit: SubmitHandler<BundleSurchargeForm> = useCallback(food => {
        if (!bundle) {
            throw new Error(`no bundle for bundle id ${bundleId}`);
        }

        const uploadSig$ = (signatureRef.current && !signatureRef.current.isEmpty())
            ? canvasToBlob(signatureRef.current.getTrimmedCanvas())
                .then(blob => {
                    const lp: LocalPhoto = {id: uuidv7(), file: blob};
                    return uploadPhoto({authenticator, queryClient, photo: lp});
                })
            : Promise.resolve(null);

        const bagged = omitFalsiness(food);
        const patch = makePatch({bundle, surchargeBag: bagged});
        const firePatch$ = patch ? performPatch({bundleId, patch}) : Promise.resolve(undefined);

        let fireJobPatch$: Promise<void> | Promise<PatchJobResponse> = Promise.resolve();
        if (food.distanceFee !== shinwaJobExtras?.distanceFee) {
            const nextShinwaJobExtras: ShinwaJobExtras = {...shinwaJobExtras, distanceFee: food.distanceFee};
            const nextJobExtras: WideJobExtras = {...jobTqrv.data?.extras, shinwa: nextShinwaJobExtras};
            fireJobPatch$ = performJobPatch({jobId: bundle.job.id, patch: {extras: nextJobExtras}});
        }

        return Promise.all([
            Promise.all([uploadSig$, stampFieldApproved()])
                .then(([sigrv, stamprv]) => {
                    if (sigrv) {
                        return attachPhotoToFieldApprovalStamp({
                            subjectClass: "Bundle",
                            subjectId: bundleId,
                            stampId: stamprv.stamp.id,
                            photoSlug: sigrv.photoSlug,
                        });
                    }
                }),
            firePatch$,
            fireJobPatch$,
        ]).then(() =>
            navigate({to: "/driver/next", params: {}, replace: true})
        ).catch((xc: unknown) => {
            const error = xc instanceof Error ? xc :
                new Error("field approval failed", {cause: xc});
            bomb(error);
        });
    }, [attachPhotoToFieldApprovalStamp, authenticator, bomb, bundle, bundleId,
        jobTqrv.data?.extras, navigate, performJobPatch, performPatch, queryClient,
        shinwaJobExtras, stampFieldApproved]);

    const onSubmit = (evt: FormEvent<HTMLFormElement>) => {
        void (handleSubmit(onValidSubmit))(evt);
    };

    if (!bundleTqrv.isSuccess) {
        return <SuspenseDialog message="fetching bundle"/>;
    }

    return <Sheet>
        <Divider sx={{
            fontSize: "lg",
            bgcolor: "primary.softBg",
            color: "primary.softColor"
        }}>{t("bundleSurchargeForm.subheader")}</Divider>
        <form id="fieldSurchargeApproval>" onSubmit={onSubmit}>
            <Stack direction="column" spacing={1.6} sx={{alignItems: "center", pb: 1.6}}>
                {sysConfTqrv.data?.fieldConfirmSurcharges
                    ? <Stack direction="column" spacing={0.8} sx={{alignItems: "center"}}>
                        <Stack direction="row" spacing={0.8}>
                            <Stack component="fieldset" disabled={readonly}>
                                <legend>{t("bundleSurchargeForm.carrierFieldset")}</legend>
                                <IntegerField control={control} fieldName="carrierBringCount"/>
                                <IntegerField control={control} fieldName="carrierTakeCount"/>
                            </Stack>
                            <Stack component="fieldset" disabled={readonly}>
                                <legend>{t("bundleSurchargeForm.bagFieldset")}</legend>
                                <IntegerField control={control} fieldName="bagBringCount"/>
                                <IntegerField control={control} fieldName="bagTakeCount"/>
                                <IntegerField control={control} fieldName="emptyBagTakeCount"/>
                            </Stack>
                            <Stack component="fieldset" disabled={readonly}>
                                <legend>{t("bundleSurchargeForm.assistFieldset")}</legend>
                                <IntegerField control={control} fieldName="assistBringCount"/>
                                <IntegerField control={control} fieldName="assistTakeCount"/>
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={0.8}>
                            {/*<StringField control={control} fieldName="packingTime" readonly={readonly}/>*/}
                            <IntegerField control={control} fieldName="containerBringCount" readonly={readonly}/>
                            <IntegerField control={control} fieldName="containerTakeCount" readonly={readonly}/>
                        </Stack>
                        <Grid container sx={{
                            columnGap: 1.8,
                            rowGap: 1.2,
                            px: 1,
                        }}>
                            {booleanFieldNames.map(fieldName =>
                                <Grid sx={{display: "flex", flexGrow: 1}}
                                      key={fieldName}
                                      size={{xs: 3}}>
                                    <BooleanField control={control} fieldName={fieldName} readonly={readonly}/>
                                </Grid>)}
                        </Grid>
                    </Stack> : null}
                <Stack direction="row" spacing={1.8} sx={{
                    border: 2,
                }}>
                    <SignatureCanvas
                        ref={signatureRef}
                        penColor={theme.palette.primary[800]}
                        canvasProps={{width: 500, height: 200}}/>
                    <IconButton type="button" onClick={() => {
                        if (signatureRef.current) {
                            signatureRef.current.clear();
                        }
                    }} sx={{p: 1}} color="warning" variant="soft">
                        <FontAwesomeIcon icon={faEraser} size="2x"/>
                    </IconButton>
                </Stack>

                <ActionButton type="submit" sx={{fontSize: theme.fontSize.xl, py: 2, px: 6}}
                              color="success" variant="solid">
                    {t("fieldApprove")}
                </ActionButton>
            </Stack>
        </form>
    </Sheet>
}

